import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage, request } from '../api';

interface VersionInfo {
  minimum: string;
  downloadUrl: string | null;
  /** The portal's fixed /download page — what clients hand to new bidders. */
  shareUrl: string;
  inUse: Array<{ version: string; bidders: number }>;
}

export function Settings(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [nextVersion, setNextVersion] = useState('');
  const [nextUrl, setNextUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  const versions = useQuery({
    queryKey: ['admin', 'desktop-version'],
    queryFn: () => request<VersionInfo>('GET', '/admin/desktop-version'),
  });
  const live = versions.data;

  // Pre-filled with what is live: the two are always saved together, so
  // changing one should not mean retyping the other.
  useEffect(() => {
    if (!live) return;
    setNextVersion((value) => value || live.minimum);
    setNextUrl((value) => value || (live.downloadUrl ?? ''));
  }, [live]);

  const save = useMutation({
    mutationFn: () =>
      request<{ version: string; downloadUrl: string }>('POST', '/admin/desktop-version', {
        version: nextVersion.trim(),
        downloadUrl: nextUrl.trim(),
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'desktop-version'] });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const version = nextVersion.trim();
  const url = nextUrl.trim();
  // The server checks this too; here it only keeps Save from sending a request
  // that is certain to fail.
  const urlValid = /^https?:\/\/\S+$/i.test(url);
  const unchanged =
    live !== undefined && version === live.minimum && url === (live.downloadUrl ?? '');

  // Installed builds this change would newly lock out. Shown before saving,
  // because this is a lever that stops people working.
  const wouldBlock =
    version && live
      ? live.inUse.filter(
          (row) => compare(row.version, version) < 0 && compare(row.version, live.minimum) >= 0,
        )
      : [];

  return (
    <>
      <header className="page-head">
        <h1>Desktop version</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Blocks outdated desktop builds and tells bidders where to download the new one.
        </p>
      </header>

      {error && <p className="error small">{error}</p>}

      <section className="card">
        <strong>Minimum version and download link</strong>
        <p className="muted small" style={{ marginTop: 4 }}>
          Builds older than the minimum stop working and show the download link instead. Takes
          effect within seconds — no redeploy. Upload the new installer first, then save its link
          together with the new minimum.
        </p>

        <div className="field" style={{ marginTop: 10 }}>
          <label>Minimum version</label>
          <input
            value={nextVersion}
            placeholder="e.g. 1.4.2"
            onChange={(e) => setNextVersion(e.target.value)}
          />
          <div className="muted small">Live: {live?.minimum ?? '…'}</div>
        </div>

        <div className="field">
          <label>Installer download link</label>
          <input
            value={nextUrl}
            placeholder="https://…/BidManager-1.4.2-x64.exe"
            onChange={(e) => setNextUrl(e.target.value)}
          />
          {/* Clickable, so an administrator can confirm the live link really downloads. */}
          <div className="muted small" style={{ wordBreak: 'break-all' }}>
            Live:{' '}
            {!live ? (
              '…'
            ) : live.downloadUrl && /^https?:\/\//i.test(live.downloadUrl) ? (
              <a href={live.downloadUrl} target="_blank" rel="noreferrer">
                {live.downloadUrl}
              </a>
            ) : (
              'not set — bidders are told to ask their client'
            )}
          </div>
          {url && !urlValid && <div className="error small">Must be a full http(s) link.</div>}
        </div>

        <p className="warn small">
          The linked installer must be this version or newer. An older file leaves bidders stuck:
          they install it, restart, and are blocked again.
        </p>

        <button
          className="primary"
          onClick={() => save.mutate()}
          disabled={!version || !urlValid || unchanged || save.isPending}
        >
          Save
        </button>

        {wouldBlock.length > 0 && (
          <div className="card" style={{ borderColor: 'var(--warn)', marginTop: 10 }}>
            <span className="warn small">
              This would immediately stop{' '}
              {wouldBlock.reduce((sum, row) => sum + row.bidders, 0)} bidder(s) on{' '}
              {wouldBlock.map((row) => row.version).join(', ')} until they update.
            </span>
          </div>
        )}

        {live && live.inUse.length > 0 && (
          <table style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Installed version</th>
                <th>Bidders</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {live.inUse.map((row) => (
                <tr key={row.version}>
                  <td>{row.version}</td>
                  <td className="muted">{row.bidders}</td>
                  <td>
                    {compare(row.version, live.minimum) < 0 ? (
                      <span className="badge warn">Blocked</span>
                    ) : (
                      <span className="badge ok">Supported</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card" style={{ marginTop: 16 }}>
        <div className="row">
          <strong>Bidder download link</strong>
          {live && (
            <button
              onClick={() => {
                void navigator.clipboard.writeText(live.shareUrl).then(() => setShareCopied(true));
              }}
            >
              {shareCopied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
        <p className="muted small" style={{ marginTop: 4 }}>
          The fixed link clients share with new bidders — shown on their Bidders page and in invite
          emails. It always opens the installer saved above.
        </p>
        <code className="small" style={{ wordBreak: 'break-all' }}>
          {live?.shareUrl ?? '…'}
        </code>
      </section>
    </>
  );
}

/** Enough ordering for the UI hint; the server does the authoritative check. */
function compare(a: string, b: string): number {
  const parse = (value: string) => value.split(/[.-]/).map((part) => Number(part) || 0);
  const left = parse(a);
  const right = parse(b);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
